import React, { useEffect, useState } from "react";
import { Layout, Menu, Empty, Spin, Typography, Drawer, Button } from "antd";
import {
  ApartmentOutlined, CompassOutlined, FolderOutlined, FileImageOutlined,
  MenuOutlined, KeyOutlined, ThunderboltOutlined, ProfileOutlined, FunctionOutlined,
  SettingOutlined, ControlOutlined, UserOutlined, FileOutlined, ApiOutlined, LayoutOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import KeyboardShortcutsView from "../BIMFramework/KeyboardShortcutsView.js";
import MarkdownContent from "../BIMFramework/MarkdownContent.js";
import useIsMobile from "../../hooks/useIsMobile.js";

const { Sider, Content } = Layout;
const { Title } = Typography;

// Learn — technical lessons on using the design software itself (ORD/MicroStation/ProjectWise),
// as opposed to BIM Framework's BEP-facing project standards. Deliberately the same
// content-folder-driven shape as BIM Framework (pages/learn/ scanned at request time, no code
// change to add a section) — this is the seed: right now it's Disciplines/Transportation/Custom
// Menus and Superelevation (both moved out of BIM Framework, where they didn't really belong —
// software configuration/usage references, not BEP-facing project documentation), with more
// titles meant to be added the same way later. MarkdownContent/KeyboardShortcutsView are reused
// directly from BIMFramework/ rather than duplicated — both are already content-shape-agnostic
// (BIMFramework/index.js is the only place that adds BEP-specific behavior on top of them).
const KEYBOARD_SHORTCUTS_KEY = "Disciplines/Transportation/Custom Menus/Keyboard Shortcuts";

// Curated icons for the known headlines; anything new falls back to a generic folder/page icon.
const ICONS = {
  "Overview":       <ProfileOutlined />,
  "Disciplines":    <ApartmentOutlined />,
  "Transportation": <CompassOutlined />,
  "Custom Menus":   <MenuOutlined />,
  "Keyboard Shortcuts": <KeyOutlined />,
  "Quick Access":       <ThunderboltOutlined />,
  "Superelevation":     <FunctionOutlined />,
  "Supplemental":             <ReadOutlined />,
  "Interface":                <ApiOutlined />,
  "CAD Environment":          <LayoutOutlined />,
  "Settings":                 <ControlOutlined />,
  "Design Software Settings": <SettingOutlined />,
  "User Settings":            <UserOutlined />,
  "Design File Settings":     <FileOutlined />,
};
const iconFor = (label, hasChildren) => ICONS[label] || (hasChildren ? <FolderOutlined /> : <FileImageOutlined />);

function buildMenuItems(nodes) {
  return nodes.map((n) => {
    const hasChildren = n.children && n.children.length > 0;
    return {
      key:   n.key,
      icon:  iconFor(n.label, hasChildren),
      label: n.label,
      ...(hasChildren ? { children: buildMenuItems(n.children) } : {}),
    };
  });
}

function firstLeafKey(nodes) {
  for (const n of nodes) {
    if (!n.children || n.children.length === 0) return n.key;
    const found = firstLeafKey(n.children);
    if (found) return found;
  }
  return null;
}

const encodeSection = (key) => key.split("/").map(encodeURIComponent).join("/");

export default function Learn() {
  const [tree, setTree]                 = useState([]);
  const [treeLoading, setTreeLoading]   = useState(true);
  const [selectedKey, setSelectedKey]   = useState(null);
  const [content, setContent]           = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    fetch("/api/learn/tree")
      .then((r) => r.json())
      .then((data) => {
        const t = data.tree || [];
        setTree(t);
        setSelectedKey(firstLeafKey(t));
      })
      .catch(() => setTree([]))
      .finally(() => setTreeLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedKey) { setContent(null); return; }
    setContentLoading(true);
    fetch(`/api/learn/content/${encodeSection(selectedKey)}`)
      .then((r) => r.json())
      .then(setContent)
      .catch(() => setContent({ description: "", images: [] }))
      .finally(() => setContentLoading(false));
  }, [selectedKey]);

  const menuItems    = buildMenuItems(tree);
  const sectionTitle = selectedKey ? selectedKey.split("/").pop() : null;

  const menuBody = (
    <>
      <div style={{ padding: "26px 20px 18px" }}>
        <Title level={4} style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <em style={{ fontStyle: "normal", color: "#4f9cf9" }}>Learn</em>
        </Title>
      </div>
      {treeLoading ? (
        <div style={{ padding: 24, textAlign: "center" }}><Spin /></div>
      ) : tree.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty description="No lessons yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : (
        <Menu
          mode="inline"
          theme="dark"
          style={{ background: "transparent", borderRight: "none" }}
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={({ key }) => { setSelectedKey(key); setNavOpen(false); }}
        />
      )}
    </>
  );

  const sectionHeader = (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
      {isMobile && (
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setNavOpen(true)}
          style={{ flexShrink: 0, marginLeft: -8 }}
        />
      )}
      <Title level={3} style={{ margin: 0 }}>{sectionTitle}</Title>
    </div>
  );

  return (
    <Layout style={{ height: "100%", background: "transparent" }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width="82%"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          closable={false}
          styles={{ body: { padding: 0 }, header: { display: "none" } }}
        >
          {menuBody}
        </Drawer>
      ) : (
        <Sider width={286} style={{ background: "rgba(255,255,255,0.02)", borderRight: "1px solid rgba(255,255,255,0.08)" }}>
          {menuBody}
        </Sider>
      )}

      <Content style={{ overflowY: "auto", padding: isMobile ? "20px 16px 40px" : "40px 56px" }}>
        {contentLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : !selectedKey ? (
          <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
            <Empty description="No lessons yet — add a folder under pages/learn/" />
          </div>
        ) : selectedKey === KEYBOARD_SHORTCUTS_KEY ? (
          <div style={{ maxWidth: 1000 }}>
            {sectionHeader}
            <KeyboardShortcutsView
              content={content}
              imageUrlFor={(img) => `/api/learn/image/${encodeSection(selectedKey)}/${encodeURIComponent(img.name)}?v=${img.version}`}
            />
          </div>
        ) : (
          <div style={{ maxWidth: 900 }}>
            {sectionHeader}

            {content?.description || content?.images?.length > 0 || content?.files?.length > 0 ? (
              <MarkdownContent
                text={content.description}
                images={content.images}
                files={content.files}
                imageUrlFor={(img) => `/api/learn/image/${encodeSection(selectedKey)}/${encodeURIComponent(img.name)}?v=${img.version}`}
                downloadUrlFor={(file) => `/api/learn/download/${encodeSection(selectedKey)}/${encodeURIComponent(file.name)}`}
              />
            ) : (
              <Empty description="No content yet for this section" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
            )}
          </div>
        )}
      </Content>
    </Layout>
  );
}
